<template>
  <div :class="{'d-flex ga-2 justify-center': showAi || !showEntities, 'height-transition-container': true }">
    <div :class="{'show-on-hover-item d-flex align-center ga-1 h-100': true, 'pt-2': showAi || !showEntities}">
      <v-tooltip v-if="showEntities" location="bottom">
        <template v-slot:activator="{ props: props }">
          <v-btn
            v-bind="props"
            class="show-on-hover-item add-schema-btn captures-description defers-schema-blur"
            variant="plain"
            icon="mdi-plus"
            size="small"
            @mousedown="onAddFieldMousedown"
            @click="onAddField(event.id, card.id)"
            :disabled="store.state.isWorkflowDisabled"
          />
        </template>
        <span>Add field</span>
      </v-tooltip>
      <v-tooltip v-if="showAi && showEntities" location="bottom">
        <template v-slot:activator="{ props: props }">
          <v-btn
            v-bind="props"
            class="show-on-hover-item add-schema-btn"
            variant="plain"
            icon="mdi-creation"
            size="small"
            @click="createTableWithAi()"
            :disabled="store.state.isWorkflowDisabled"
          />
        </template>
        <span>Generate fields with AI</span>
      </v-tooltip>
      <entity-field-selector
        v-if="showEntities"
        :card="{ ...card, entityId }"
        @onUpdateFields="(fields) => updateFields(event.id, card.id, fields)"
        :disabled="!entityId"
        :positioningActivator="menuPositioningActivator"
        :location="card.cardType?.domainModelRole === DomainModelRole.READ_MODEL ? 'right top' : 'right center'"
      >
        <v-tooltip v-if="showFilters" location="bottom" open-delay="100">
          <template v-slot:activator="{ on, props }">
            <v-btn
              class="show-on-hover-item"
              v-bind="props"
              v-on="{ ...on }"
              size="small"
              variant="plain"
              tabindex="-1"
              icon="mdi-list-status"
              :style="{ 'cursor': entityId ? 'pointer' : 'not-allowed' }"
              :disabled="store.state.isWorkflowDisabled"
            />
          </template>
          <span>{{ entityId ? 'Select fields' : 'Disabled because no entity is selected' }}</span>
        </v-tooltip>
      </entity-field-selector>
      <v-tooltip location="bottom" open-delay="100">
        <template v-slot:activator="{ on, props }">
          <v-btn
            class="show-on-hover-item"
            v-bind="props"
            v-on="{ ...on }"
            size="small"
            variant="plain"
            @click="removeCardAndModel(event, card)"
            tabindex="-1"
            icon="mdi-trash-can-outline"
            :loading="loadingDeleteModel"
            :disabled="store.state.isWorkflowDisabled"
          />
        </template>
        <span>Delete card</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { computed, getCurrentInstance } from 'vue'
import EntityFieldSelector from '@/components/EntityFieldSelector'
import useDomainModel from '@/composables/useDomainModel'
import { cardHelpers } from '@/mixins/cardHelpers'
import usePrompts from '@/composables/usePrompts'
import workflowApi from '@/api/workflowApi'
import { DomainModelRole } from '@/constants/cardTypes'
import { parseCommandFields, parseReadModelFields } from '@/shared/utils/workflowSpec'
import { SchemaType } from '@/constants/schema'
import { sanitizeCardDescription } from '@/utils/sanitize'
import { runAiGeneration } from '@/utils/helpHero'

const {
  event,
  card,
  showAi,
  showFilters,
  menuPositioningActivator
} = defineProps({
  event: Object,
  card: Object,
  showAi: Boolean,
  showFilters: Boolean,
  menuPositioningActivator: Object
})

const store = useStore()
const { proxy } = getCurrentInstance()

const workflow = computed(() => store.state.workflow.workflow)
const svgGraph = computed(() => store.getters['svgGraphData/svgGraphData'])
const showEntities = computed(() => store.state.domainModelShowEntities)

const entityId = computed(() => {
  if (card.cardType?.domainModelRole === DomainModelRole.COMMAND) {
    return cardHelpers.methods.getEntityCard(event)?.schemaId
  } else {
    const schema = card.schemaId
      ? workflow.value.schemas?.find((s) => s.id === card.schemaId)
      : null
    return schema?.entityId
  }
})

const { addField, updateFields, removeCardAndModel, loadingDeleteModel } = useDomainModel()
const { getReadModelFromWorkflow, getReadModelFromEntity, getCommand } = usePrompts()

let skipNextAddField = false

const onAddFieldMousedown = () => {
  skipNextAddField = !!document.activeElement?.id?.startsWith('combobox-input-')
}

const onAddField = (...params) => {
  if (skipNextAddField) {
    skipNextAddField = false

    return
  }

  addField(...params)
}

// Its saves are the AI's, not the person's own edits (activation tracking).
const createTableWithAi = () => runAiGeneration(generateTableWithAi)

const generateTableWithAi = async () => {
  proxy.$loadingState.start()

  try {
    let newSchema

    if (card.cardType?.domainModelRole === DomainModelRole.READ_MODEL) {
      const querySchema = card.schemaId
        ? workflow.value.schemas?.find((s) => s.id === card.schemaId)
        : null
      const selectedEntity = querySchema?.entityId
        ? (workflow.value.schemas ?? []).find((entity) => entity.id === querySchema.entityId)
        : null
      const response = selectedEntity
        ? await getReadModelFromEntity(event.id, selectedEntity)
        : await getReadModelFromWorkflow(event.id)

      if (!response?.readModel?.length) {
        store.commit('snackbar/showMessage', {
          content: 'Error adding',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
        proxy.$loadingState.stop()

        return
      }

      newSchema = {
        name: sanitizeCardDescription(card.description) || '',
        type: SchemaType.QUERY,
        description: response.readModelDescription?.trim() || undefined,
        cardinality: response.cardinality,
        fields: parseReadModelFields(response.readModel)
      }
    }

    if (card.cardType?.domainModelRole === DomainModelRole.COMMAND) {
      const response = await getCommand(event.id)

      if (!response?.command?.length) {
        store.commit('snackbar/showMessage', {
          content: 'Error adding',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
        proxy.$loadingState.stop()

        return
      }

      newSchema = {
        name: sanitizeCardDescription(card.description) || '',
        type: SchemaType.COMMAND,
        fields: parseCommandFields({ fields: response.command, required: response.required })
      }
    }

    await workflowApi.addSchemaAndLinkCard({
      schema: newSchema,
      cardId: card.id,
      eventId: event.id,
      workflow: workflow.value,
      svgGraph: svgGraph.value
    })

    store.commit('snackbar/showMessage', {
      content: 'Completed',
      timeout: 3000,
      color: 'blue'
    }, { root: true })
  } catch (error) {
    if (error.name !== 'AbortError') {
      const itemType = card.cardType?.domainModelRole === DomainModelRole.READ_MODEL ? 'readmodel' : 'command'
      const itemName = card.cardType?.domainModelRole === DomainModelRole.READ_MODEL ? 'Generate Read Model' : 'Generate Command'

      await store.dispatch('workflow/openRetryDialog', {
        failedItems: [{ type: itemType, name: itemName }],
        retryCallback: () => createTableWithAi()
      })
    }
  } finally {
    proxy.$loadingState.stop()
  }
}
</script>
